/**
 * SSO Receiver Page - YUL ON TAP
 * 
 * Receives SSO token from redirect, validates it, creates session,
 * and redirects to the Restaurant Dashboard.
 * 
 * This is the mock SSO receiver for demo/testing purposes.
 * In production, this would be handled by the live restaurant system.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ssoAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export default function SSOReceiver() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithSSO } = useAuth();
  const [status, setStatus] = useState('validating');
  const [error, setError] = useState(null);

  const validateAndCreateSession = useCallback(async (token) => {
    try {
      setStatus('validating');
      
      // Get the industry from URL query param (belt and suspenders approach)
      const industryParam = searchParams.get('industry');
      
      // Validate SSO token and get session
      const response = await ssoAPI.validate(token);
      const { user, session_token, industry_portal } = response.data;
      
      // Priority: 1. API response industry_portal, 2. URL param, 3. user data, 4. fallback
      const industry = industry_portal || industryParam || user.industry_type || 'restaurant';
      loginWithSSO(user, session_token, industry);
      
      setStatus('success');
      
      // Redirect to Restaurant Dashboard
      setTimeout(() => {
        navigate('/restaurant-dashboard', { replace: true });
      }, 500);
      
    } catch (err) {
      console.error('SSO validation failed:', err);
      setStatus('error');
      setError(err.response?.data?.detail || err.message || 'SSO validation failed');
    }
  }, [loginWithSSO, navigate, searchParams]);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('No SSO token provided');
      return;
    }

    validateAndCreateSession(token);
  }, [searchParams, validateAndCreateSession]);

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
      <div className="text-center space-y-4">
        {status === 'validating' && (
          <>
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
            <p className="text-white text-lg">Validating credentials...</p>
            <p className="text-[hsl(215,20%,65%)] text-sm">Please wait while we log you in</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-white text-lg">Authentication successful</p>
            <p className="text-[hsl(215,20%,65%)] text-sm">Redirecting to dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-white text-lg">Authentication failed</p>
            <p className="text-red-400 text-sm">{error}</p>
            <button 
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
